import React, { useEffect, useState, useMemo } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  executeTask,
  deleteAction,
  updateTask,
  getAllUserTasks,
  addTaskReply,
  fetchAllTeamMemberTask,
} from "../../features/tasks/taskSlice";
import { Icons } from "../../constants/icon-library";
import { toast } from "react-toastify";
import Task from "./Task";
import { fetchSettingDetailsByUserId } from "../../features/settings/settingsSlice";
import LoadingComponent from "../../components/common/LoadingComponent";
import { confirmationModal } from "../../constants/confirmation.constants";
import { getParticipants } from "../../features/participant/participantSlice";
import { fetchAllMembers } from "../../features/team-member/teamMemberSlice";
import { CATEGORY_TABS } from "../../constants/constants";

const CATEGORY_LABELS = CATEGORY_TABS.reduce((acc, tab) => {
  acc[tab.key] = tab.label;
  return acc;
}, {});

const getCategoryLabel = (category) => {
  if (!category || category === "uncategorized") return "Uncategorized";

  return (
    CATEGORY_LABELS[category] ||
    category
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
};

const TasksList = ({
  fetchTasksAction,
  taskDocuments,
  loading: externalLoading,
  title: externalTitle,
  groupByCategory = false,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const rawCategory = searchParams.get("category") || "";
  const category = rawCategory === "all" ? "" : rawCategory;
  const isTeamMemberPage = location.pathname === "/my-tasks";

  const [selectedTasks, setSelectedTasks] = useState([]);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editedTaskName, setEditedTaskName] = useState("");
  const [editedTaskDescription, setEditedTaskDescription] = useState("");
  const [executingTaskIds, setExecutingTaskIds] = useState(new Set());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [replyingTaskId, setReplyingTaskId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReplyIds, setSendingReplyIds] = useState(new Set());

  const { allUserTasks, loading } = useSelector((state) => state.task);
  const { userSettings } = useSelector((state) => state.setting);
  const { participants: allParticipantsRaw = [] } = useSelector(
    (state) => state.participants,
  );
  const allParticipants = useMemo(
    () => allParticipantsRaw.filter((p) => p.platforms?.length > 0),
    [allParticipantsRaw],
  );
  const { members } = useSelector((state) => state.teamMember);
  const { user } = useSelector((state) => state.user);

  const refetchTasks = () => {
    if (fetchTasksAction) {
      dispatch(fetchTasksAction());
      return;
    }

    const taskAction = isTeamMemberPage
      ? fetchAllTeamMemberTask
      : getAllUserTasks;

    dispatch(taskAction(category || undefined));
  };

  useEffect(() => {
    refetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamMemberPage, category, fetchTasksAction]);

  useEffect(() => {
    if (!user?.org) return;

    if (!userSettings?.length) {
      dispatch(fetchSettingDetailsByUserId({ accountId: user.org }));
    }
    if (!allParticipantsRaw?.length) {
      dispatch(getParticipants());
    }
    if (!members?.length) {
      dispatch(fetchAllMembers());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org]);

  const sourceTasks = taskDocuments ?? allUserTasks;
  const isLoading = externalLoading ?? loading;

  const allTasks = useMemo(() => {
    if (!sourceTasks?.length) return [];
    return sourceTasks.flatMap((taskDoc) => {
      const actions = Array.isArray(taskDoc?.actions)
        ? taskDoc.actions
        : [taskDoc];
      const integrationName =taskDoc?.integrationSettingId?.integrationId?.name || "";
      return actions.map((action) => ({
        ...action,
        category: action.category || taskDoc.category || "uncategorized",
        taskId: action.taskId || taskDoc.uuid,
        meetingId: action.meetingId || taskDoc.uuid,
        meetingTitle: taskDoc.title,
        meetingParticipants: taskDoc.participants || [],
        integrationName
      }));
    });
  }, [sourceTasks]);

  const filteredTasks = useMemo(() => {
    if (!startDate && !endDate) return allTasks;
    return allTasks.filter((task) => {
      if (!task.createdAt) return true;
      const taskDate = new Date(task.createdAt);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      if (start && taskDate < start) return false;
      if (end) {
        end.setHours(23, 59, 59, 999);
        if (taskDate > end) return false;
      }
      return true;
    });
  }, [allTasks, startDate, endDate]);

  const tasksByCategory = useMemo(() => {
    if (!groupByCategory) return [];

    return filteredTasks.reduce((groups, task) => {
      const taskCategory = task.category || "uncategorized";
      const existingGroup = groups.find(
        (group) => group.category === taskCategory,
      );

      if (existingGroup) {
        existingGroup.tasks.push(task);
      } else {
        groups.push({ category: taskCategory, tasks: [task] });
      }

      return groups;
    }, []);
  }, [filteredTasks, groupByCategory]);

  const handleTaskUpdate = async (uuid, payload) => {
    const taskItem = allTasks.find((t) => t.uuid === uuid);
    if (!taskItem) return;

    const result = await dispatch(
      updateTask({ uuid, taskId: taskItem.taskId, ...payload }),
    );

    if (updateTask.fulfilled.match(result)) {
      refetchTasks();
      toast.success("Task updated successfully");
    } else {
      toast.error("Failed to update task");
    }
  };

  const handleExecute = async (actionIds) => {
    const matchedActions = allTasks.filter((t) => actionIds.includes(t.uuid));
    setExecutingTaskIds((prev) => {
      const updated = new Set(prev);
      matchedActions.forEach((a) => updated.add(a.uuid));
      return updated;
    });
    setSelectedTasks([]);

    for (const action of matchedActions) {
      const result = await dispatch(executeTask({ taskData: action }));

      if (executeTask.fulfilled.match(result) && result.payload?.success) {
        toast.success("Task executed successfully");
      } else {
        toast.error(result.payload?.message || "Failed to execute task");
      }

      setExecutingTaskIds((prev) => {
        const updated = new Set(prev);
        updated.delete(action.uuid);
        return updated;
      });
    }

    refetchTasks();
  };

  const handleDeleteTask = async (taskItem) => {
    if (!taskItem?.taskId || !taskItem?.uuid) return;
    const result = await confirmationModal(
      "Are you sure?",
      "This will delete it permanently.",
    );

    if (result.isConfirmed) {
      const res = await dispatch(
        deleteAction({ taskId: taskItem.taskId, actionUuid: taskItem.uuid }),
      );

      if (deleteAction.fulfilled.match(res)) {
        refetchTasks();
        toast.success("Task removed successfully.");
      } else {
        toast.error("Failed to remove task.");
      }
    }
  };

  const handleSaveEdit = async (taskItem) => {
    const taskName = editedTaskName.trim();
    const description = editedTaskDescription.trim();
    if (!taskName || description.length < 10) return;

    const result = await dispatch(
      updateTask({
        uuid: taskItem.uuid,
        taskId: taskItem.taskId,
        status: taskItem.status,
        name: taskName,
        description,
      }),
    );

    if (updateTask.fulfilled.match(result)) {
      refetchTasks();
    } else {
      toast.error("Failed to save task");
    }

    setEditingTaskId(null);
    setEditedTaskName("");
    setEditedTaskDescription("");
  };

  const handleStartEdit = (taskItem) => {
    setEditingTaskId(taskItem.uuid);
    setEditedTaskName(taskItem.name);
    setEditedTaskDescription(taskItem.description);
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditedTaskName("");
    setEditedTaskDescription("");
  };

  const handleKeyDown = (e, taskItem) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(taskItem);
    } else if (e.key === "Escape") handleCancelEdit();
  };

  const handleToggleTask = (taskId) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId],
    );
  };

  const handleToggleAll = () => {
    setSelectedTasks(
      selectedTasks.length === allTasks.length
        ? []
        : allTasks.map((t) => t.uuid),
    );
  };

  const handleStartReply = (taskItem) => {
    setReplyingTaskId(taskItem.uuid);
    setReplyText("");
  };

  const handleCancelReply = () => {
    setReplyingTaskId(null);
    setReplyText("");
  };

  const handleReplyKeyDown = (e, taskItem) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply(taskItem);
    } else if (e.key === "Escape") {
      handleCancelReply();
    }
  };

  const handleSendReply = async (taskItem) => {
    const reply = replyText.trim();
    if (!reply) {
      toast.error("Reply cannot be empty");
      return;
    }
    if (!taskItem?.taskId || !taskItem?.uuid) return;

    setSendingReplyIds((prev) => new Set(prev).add(taskItem.uuid));

    const result = await dispatch(
      addTaskReply({
        taskId: taskItem.taskId,
        actionId: taskItem.uuid,
        reply,
      }),
    );

    setSendingReplyIds((prev) => {
      const updated = new Set(prev);
      updated.delete(taskItem.uuid);
      return updated;
    });

    if (addTaskReply.fulfilled.match(result)) {
      refetchTasks();
      toast.success("Reply sent successfully");
      setReplyingTaskId(null);
      setReplyText("");
    } else {
      toast.error(result.payload?.message || "Failed to send reply");
    }
  };

  if (isLoading && !sourceTasks?.length) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <LoadingComponent loading={true} size={15} />
      </div>
    );
  }

  const pageTitle =
    externalTitle ||
    (category && CATEGORY_LABELS[category]
      ? CATEGORY_LABELS[category]
      : "All Tasks");

  return (
    <div
      className="content"
      style={{ maxWidth: "100%", padding: "4px 20px", marginTop: "40px" }}
    >
      <div className="pb-5 pt-3">
        <div className="bg-body-emphasis rounded-3 p-4 border">
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <h3 className="mb-0 fw-semibold d-flex align-items-center gap-2 text-body">
              <i className="bi bi-list-check" />
              {pageTitle}
              <span
                className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill fw-normal"
                style={{ fontSize: 13 }}
              >
                {filteredTasks.length}
              </span>
            </h3>

            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-1">
                <label className="form-label mb-0 text-muted small">From</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="form-label mb-0 text-muted small">To</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {filteredTasks.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2" />
              <p className="mb-0">No tasks found across any integrations.</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-4">
              {(groupByCategory
                ? tasksByCategory
                : [{ category: "all", tasks: filteredTasks }]
              ).map(({ category: taskCategory, tasks }) => (
                <div key={taskCategory}>
                  {groupByCategory && (
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h4 className="mb-0 fw-semibold">
                        {getCategoryLabel(taskCategory)}
                      </h4>
                      <span className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill fw-normal">
                        {tasks.length}
                      </span>
                    </div>
                  )}
                  <Task
                    actions={tasks}
                    groupByPlatform={false}
                    selectedTasks={selectedTasks}
                    editingTaskId={editingTaskId}
                    editedTaskName={editedTaskName}
                    editedTaskDescription={editedTaskDescription}
                    onStartEdit={handleStartEdit}
                    onCancelEdit={handleCancelEdit}
                    onSaveEdit={handleSaveEdit}
                    onDeleteTask={handleDeleteTask}
                    onKeyDown={handleKeyDown}
                    onTaskNameChange={setEditedTaskName}
                    onDescriptionChange={setEditedTaskDescription}
                    onSend={handleExecute}
                    settings={userSettings}
                    onToggleTask={handleToggleTask}
                    onTogglePlatformTask={handleToggleTask}
                    onToggleAll={handleToggleAll}
                    handleToggleAll={handleToggleAll}
                    participants={allParticipants}
                    onTaskUpdate={handleTaskUpdate}
                    executingTaskIds={executingTaskIds}
                    replyingTaskId={replyingTaskId}
                    replyText={replyText}
                    sendingReplyIds={sendingReplyIds}
                    onStartReply={handleStartReply}
                    onCancelReply={handleCancelReply}
                    onReplyTextChange={setReplyText}
                    onSendReply={handleSendReply}
                    onReplyKeyDown={handleReplyKeyDown}
                    members={members}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedTasks?.length > 0 && (
        <div className="execute-bar bg-body-emphasis border-top border-translucent shadow-lg">
          <div className="container-fluid">
            <div className="d-flex align-items-center justify-content-end py-3 px-3">
              <button
                className="btn btn-primary btn-sm execute-bar__btn"
                onClick={() => handleExecute(selectedTasks)}
              >
                <i className={`wb ${Icons("resume")} me-1 align-middle`} />
                Execute
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksList;
